import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Wrap,
  WrapItem,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdBadge, MdBusiness, MdLock, MdSupervisorAccount, MdApps, MdEdit, MdInfoOutline, MdBlock, MdCheckCircle } from 'react-icons/md';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import SelectField from 'components/fields/SelectField';
import usuariosApi from 'api/usuarios';
import hotelesApi from 'api/hoteles';
import departamentosApi from 'api/departamentos';
import modulosApi from 'api/modulos';
import {
  getCurrentUser,
  isAdmin,
  esAdminDepartamento,
  puedeAdministrarUsuarios,
  getDepartamentosPermitidos,
  ADMIN_ROL_ID,
  ADMIN_REGIONAL_ROL_ID,
  ADMIN_DEPARTAMENTO_ROL_ID,
} from 'utils/permissions';

// RolId de los tres niveles de administrador: solo el admin global puede
// asignarle alguno de estos a un usuario nuevo (para que un admin regional o
// de departamento no pueda crear a alguien con más poder que él mismo).
const ROLES_ADMIN = [String(ADMIN_ROL_ID), String(ADMIN_REGIONAL_ROL_ID), String(ADMIN_DEPARTAMENTO_ROL_ID)];

interface Catalogo {
  Id: number | string;
  Name: string;
}

interface UsuarioForm {
  fullName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  rolId: string;
  departamentoId: string;
  hotelIds: string[];
  jefeId: string;
  // Scope adicional (opcional): departamentos y módulos explícitos. Vacío =
  // sin restricción adicional (se comporta igual que hoy).
  departamentoIds: string[];
  moduloIds: string[];
}

const FORM_INICIAL: UsuarioForm = {
  fullName: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  rolId: '',
  departamentoId: '',
  hotelIds: [],
  jefeId: '',
  departamentoIds: [],
  moduloIds: [],
};

// RolId del rol "usuario" (colaborador regular): es el único que dispara el
// correo de firma al jefe cuando se crea un resguardo (ver enviarEmailResguardo, Rol === 3)
const ROL_USUARIO_ID = '3';

const ROL_BADGE_COLOR: Record<string, string> = {
  administrador: 'red',
  supervisor: 'purple',
  usuario: 'blue',
};

// Explicación de qué puede hacer cada rol, mostrada al seleccionarlo al
// crear/editar un usuario. Se basa en el comportamiento real del sistema
// (quién autoriza resguardos directo vs. quién necesita revisión de un jefe,
// VeTodosDepartamentos por rol, etc.), no solo en el nombre.
const ROL_DESCRIPCION: Record<string, string> = {
  'administrador': 'Acceso total: ve todos los hoteles, departamentos y usuarios sin restricción. Es el único que puede crear otros administradores (regional o de departamento). Puede autorizar resguardos directo, sin pasar por revisión.',
  'supervisor': 'Puede autorizar resguardos directamente (sin mandarlos a revisión de un jefe) y firmar como gerente. Ve solo los hoteles y departamentos que tenga asignados.',
  'usuario': 'Colaborador regular. Al crear un resguardo, debe pasar por la revisión y firma de su jefe/supervisor antes de autorizarse — por eso es el único rol al que hay que asignarle un jefe.',
  'auditor': 'Acceso de solo lectura: puede consultar información, pero no puede crear, editar ni autorizar nada.',
  'mantenimiento': 'Mismo nivel operativo que Supervisor (puede autorizar resguardos directo, sin revisión); pensado para personal de mantenimiento.',
  'admin regional': 'Ve todos los departamentos, pero acotado a los hoteles que tenga asignados — funciona como un Administrador, pero solo dentro de su(s) hotel(es).',
  'admin de departamento': 'Acotado a su propio departamento, pero con permisos administrativos dentro de este: puede crear y gestionar usuarios de ese departamento.',
};

const validarEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// MySQL no acepta el formato ISO 8601 (con "T"/"Z"/milisegundos) que produce
// Date.toISOString(); necesita "YYYY-MM-DD HH:MM:SS".
const formatoFechaMySQL = (fecha: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())} `
    + `${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
};

// Calcula qué agregar/quitar entre las asignaciones actuales (tabla de
// relación usuario↔recurso: hotel, departamento o módulo) y lo que quedó
// marcado en el formulario, y aplica ambos lados. Se usa igual para los tres
// (solo cambia el nombre del campo Id y las funciones crear/darBaja).
const sincronizarAsignaciones = async (params: {
  originales: any[];
  campoId: string;
  nuevos: string[];
  usuarioId: number | string;
  ahora: string;
  crear: (data: any) => Promise<any>;
  darBaja: (id: any) => Promise<any>;
}): Promise<number> => {
  const { originales, campoId, nuevos, usuarioId, ahora, crear, darBaja } = params;
  const idsOriginales = originales.map((o) => String(o[campoId]));
  const aAgregar = nuevos.filter((id) => !idsOriginales.includes(id));
  const aQuitar = originales.filter((o) => !nuevos.includes(String(o[campoId])));

  // El Id lo autogenera la base de datos (AUTO_INCREMENT) — no se calcula aquí.
  // Calcularlo en el cliente (p.ej. "máximo de mis filas + 1") choca con Ids de
  // otros usuarios, ya que la tabla es global, no por-usuario.
  const resultados = await Promise.allSettled([
    ...aAgregar.map((id) => crear({
      UsuarioId: usuarioId,
      [campoId]: id,
      CreatedDate: ahora,
      LastModifiedDate: ahora,
    })),
    ...aQuitar.map((o) => darBaja(o.Id)),
  ]);
  return resultados.filter((r) => r.status === 'rejected').length;
};

const SectionTitle = ({ icon, children }: { icon: any; children: ReactNode }) => (
  <HStack spacing={2} mb={3}>
    <Icon as={icon} color="blue.500" w={5} h={5} />
    <Text fontSize="sm" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.04em">
      {children}
    </Text>
  </HStack>
);

export default function ConfigUsuarios() {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');

  const [hoteles, setHoteles] = useState<Catalogo[]>([]);
  const [roles, setRoles] = useState<Catalogo[]>([]);
  const [departamentos, setDepartamentos] = useState<Catalogo[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [usuariosVisibles, setUsuariosVisibles] = useState<any[]>([]);
  const [usuariosHoteles, setUsuariosHoteles] = useState<any[]>([]);
  const [modulos, setModulos] = useState<Catalogo[]>([]);
  const [isLoadingCatalogos, setIsLoadingCatalogos] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [departamentosTodos, setDepartamentosTodos] = useState<Catalogo[]>([]);
  const [form, setForm] = useState<UsuarioForm>(FORM_INICIAL);
  const [busqueda, setBusqueda] = useState('');
  const [filtroDepartamentos, setFiltroDepartamentos] = useState('');

  // Modo edición: null = creando un usuario nuevo. Si tiene un Id, se está
  // editando ese usuario, y aquí se guardan sus asignaciones ANTES de tocar
  // nada, para poder calcular qué agregar/quitar de hoteles/departamentos/
  // módulos al guardar (esas son tablas de relación aparte, no un campo simple).
  const [editandoId, setEditandoId] = useState<number | string | null>(null);
  const [asignacionesOriginales, setAsignacionesOriginales] = useState<{
    hoteles: any[]; departamentos: any[]; modulos: any[];
  }>({ hoteles: [], departamentos: [], modulos: [] });
  const [cargandoEdicion, setCargandoEdicion] = useState(false);
  const [cambiandoEstadoId, setCambiandoEstadoId] = useState<number | string | null>(null);
  // Jefe/supervisor que ya tenía asignado el usuario ANTES de abrir el modal
  // de edición — sirve para saber si realmente cambió al guardar. '' = no
  // tenía jefe asignado (o se está creando un usuario nuevo).
  const [jefeOriginalId, setJefeOriginalId] = useState<string>('');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const cargarCatalogos = async () => {
    setIsLoadingCatalogos(true);
    try {
      const user = getCurrentUser();

      const [hotelesRaw, rolesRaw, departamentosRaw, usuariosRaw, usuariosHotelesRaw, modulosRaw] = await Promise.all([
        hotelesApi.listado(),
        fetch('https://inventariosbackend.velasresorts.com/inventarios/roles/listado')
          .then(r => r.json())
          .catch((): any[] => []),
        departamentosApi.listado(),
        usuariosApi.listado(),
        hotelesApi.listadoUsuariosHoteles(),
        modulosApi.listado(),
      ]);

      const hotelesList: Catalogo[] = Array.isArray(hotelesRaw) ? hotelesRaw : [];
      // El Administrador global ve todos los hoteles y usuarios sin importar
      // a cuáles esté asignado él mismo — esa acotación es solo para el Admin
      // Regional/de Departamento, que sí deben quedarse a lo suyo.
      const allowed = (!isAdmin() && user?.HotelIds)
        ? String(user.HotelIds).split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
      const hotelesPermitidos = allowed.length > 0
        ? hotelesList.filter(h => allowed.includes(String(h.Id)))
        : hotelesList;

      const rolesList: Catalogo[] = Array.isArray(rolesRaw) && Array.isArray(rolesRaw[0])
        ? rolesRaw[0]
        : (Array.isArray(rolesRaw) ? rolesRaw : []);

      const usuariosList: any[] = Array.isArray(usuariosRaw) ? usuariosRaw : [];
      const usuariosUnicos = Array.from(
        new Map(usuariosList.map((u: any) => [u.Id, u])).values()
      );

      const usuariosHotelesList: any[] = Array.isArray(usuariosHotelesRaw) ? usuariosHotelesRaw : [];

      // "usuarios" (todos, sin filtrar) se usa para validar Id/correo duplicado a nivel sistema.
      // "usuariosVisibles" es lo que se muestra en la tabla: solo colaboradores de los
      // hoteles que este administrador tiene asignados (mismo criterio que "hoteles").
      const usuariosVisiblesPorHotel = allowed.length > 0
        ? usuariosUnicos.filter((u: any) =>
            usuariosHotelesList.some((uh: any) =>
              String(uh.UsuarioId) === String(u.Id) && allowed.includes(String(uh.HotelId))
            )
          )
        : usuariosUnicos;

      // Un Admin de Departamento además solo ve/gestiona colaboradores de su(s)
      // propio(s) departamento(s) (un Admin Regional ve todos, por eso su rol
      // tiene VeTodosDepartamentos=1 y esto no le acota nada).
      const departamentosList: Catalogo[] = Array.isArray(departamentosRaw) ? departamentosRaw : [];
      const departamentosPermitidos = getDepartamentosPermitidos();
      const departamentosVisibles = departamentosPermitidos === null
        ? departamentosList
        : departamentosList.filter(d => departamentosPermitidos.includes(Number(d.Id)));

      const usuariosVisiblesList = departamentosPermitidos === null
        ? usuariosVisiblesPorHotel
        : usuariosVisiblesPorHotel.filter((u: any) =>
            departamentosVisibles.some((d: any) => d.Name === u.Departamento)
          );

      setHoteles(hotelesPermitidos);
      setRoles(rolesList);
      setDepartamentos(departamentosVisibles);
      setDepartamentosTodos(departamentosList);
      setUsuarios(usuariosUnicos);
      setUsuariosVisibles(usuariosVisiblesList);
      setUsuariosHoteles(usuariosHotelesList);
      setModulos(Array.isArray(modulosRaw) ? modulosRaw : []);
    } catch (error) {
      console.error(error);
      toast({ title: 'Error al cargar catálogos', status: 'error', duration: 4000 });
    } finally {
      setIsLoadingCatalogos(false);
    }
  };

  useEffect(() => {
    cargarCatalogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actualizarCampo = (campo: keyof UsuarioForm, valor: any) => {
    setForm(prev => ({ ...prev, [campo]: valor }));
  };

  const requiereJefe = form.rolId === ROL_USUARIO_ID;

  // No tiene sentido ofrecer como jefe a alguien desactivado — no podría
  // entrar al sistema para firmar/autorizar nada.
  const jefesDisponibles = Array.from(
    new Map(
      usuariosHoteles
        .filter((uh: any) => form.hotelIds.includes(String(uh.HotelId)))
        .filter((uh: any) => {
          const usuario = usuarios.find((u: any) => String(u.Id) === String(uh.UsuarioId));
          return !usuario || usuario.Status === 'active';
        })
        .map((uh: any) => [uh.UsuarioId, { Id: uh.UsuarioId, Name: uh.Usuario }])
    ).values()
  );

  // Solo el admin global puede crear otro usuario con rol de administrador
  // (de cualquiera de los 3 niveles) — un admin regional o de departamento no
  // puede darle a alguien más poder del que él mismo tiene.
  const rolesAsignables = useMemo(() => {
    if (isAdmin()) return roles;
    return roles.filter((r: any) => !ROLES_ADMIN.includes(String(r.Id)));
  }, [roles]);

  // Mismo candado que ya aplica el backend: solo el Administrador global
  // puede editar o cambiar el estado de OTRO admin (regional o de
  // departamento) — nadie con menos jerarquía puede tocarlo.
  const ROLES_ADMIN_NOMBRES = ['administrador', 'admin regional', 'admin de departamento'];
  const esUsuarioAdminTier = (u: any): boolean =>
    ROLES_ADMIN_NOMBRES.includes(String(u.Rol || '').toLowerCase());

  // Para editar: cualquiera puede editar su propio perfil; a otro admin
  // solo lo edita el Administrador global.
  const puedeEditarUsuario = (u: any): boolean => {
    if (String(u.Id) === String(getCurrentUser()?.Id)) return true;
    if (esUsuarioAdminTier(u) && !isAdmin()) return false;
    return true;
  };

  // Para activar/desactivar: nadie puede tocarse a sí mismo (evita quedarse
  // fuera del sistema sin querer), y a otro admin solo lo toca el global.
  const puedeCambiarEstadoDe = (u: any): boolean => {
    if (String(u.Id) === String(getCurrentUser()?.Id)) return false;
    if (esUsuarioAdminTier(u) && !isAdmin()) return false;
    return true;
  };

  const rolSeleccionado = useMemo(
    () => roles.find((r: any) => String(r.Id) === form.rolId),
    [roles, form.rolId]
  );

  const departamentosFiltrados = useMemo(() => {
    const q = filtroDepartamentos.trim().toLowerCase();
    if (!q) return departamentos;
    return departamentos.filter((d: any) => String(d.Name || '').toLowerCase().includes(q));
  }, [departamentos, filtroDepartamentos]);

  const validarFormulario = (): string | null => {
    if (!form.fullName.trim()) return 'El nombre completo es obligatorio.';
    if (!form.username.trim()) return 'El usuario es obligatorio.';
    if (!form.email.trim() || !validarEmail(form.email)) return 'El correo no es válido.';
    // Al editar, la contraseña es opcional: en blanco = no cambiarla.
    if (!editandoId || form.password) {
      if (!form.password || form.password.length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
      if (form.password !== form.confirmPassword) return 'Las contraseñas no coinciden.';
    }
    if (!form.rolId) return 'Selecciona un rol.';
    if (!form.departamentoId) return 'Selecciona un departamento.';
    if (form.hotelIds.length === 0) return 'Selecciona al menos un hotel.';
    if (requiereJefe && !form.jefeId) return 'Selecciona el jefe/supervisor de este colaborador.';

    const emailDuplicado = usuarios.some(
      (u: any) => String(u.Id) !== String(editandoId)
        && String(u.Email || '').trim().toLowerCase() === form.email.trim().toLowerCase()
    );
    if (emailDuplicado) return 'Ya existe un usuario registrado con ese correo.';

    return null;
  };

  const cerrarModal = () => {
    setForm(FORM_INICIAL);
    setEditandoId(null);
    setAsignacionesOriginales({ hoteles: [], departamentos: [], modulos: [] });
    setJefeOriginalId('');
    onClose();
  };

  // Abre el modal en modo edición, precargando el formulario y las
  // asignaciones actuales de hotel/departamento/módulo/jefe de ese usuario.
  const abrirEdicion = async (usuario: any) => {
    setCargandoEdicion(true);
    try {
      const [usuariosDepartamentosRaw, usuariosModulosRaw, supervisorRaw] = await Promise.all([
        departamentosApi.listadoUsuariosDepartamentos(),
        modulosApi.listadoUsuariosModulos(),
        usuariosApi.obtenerSupervisor(usuario.Id),
      ]);

      const rolEncontrado = roles.find((r: any) => r.Name === usuario.Rol);
      const departamentoEncontrado = departamentosTodos.find((d: any) => d.Name === usuario.Departamento);

      const hotelesDelUsuario = usuariosHoteles.filter(
        (uh: any) => String(uh.UsuarioId) === String(usuario.Id)
      );
      const departamentosDelUsuario = (Array.isArray(usuariosDepartamentosRaw) ? usuariosDepartamentosRaw : [])
        .filter((ud: any) => String(ud.UsuarioId) === String(usuario.Id));
      const modulosDelUsuario = (Array.isArray(usuariosModulosRaw) ? usuariosModulosRaw : [])
        .filter((um: any) => String(um.UsuarioId) === String(usuario.Id));

      setAsignacionesOriginales({
        hoteles: hotelesDelUsuario,
        departamentos: departamentosDelUsuario,
        modulos: modulosDelUsuario,
      });

      // searchSupervisores ya solo debe traer un renglón (un jefe vigente por
      // colaborador), pero por si acaso quedó algo viejo tomamos el primero.
      const jefeActualId = Array.isArray(supervisorRaw) && supervisorRaw[0]?.SupervisorId
        ? String(supervisorRaw[0].SupervisorId)
        : '';
      setJefeOriginalId(jefeActualId);

      setForm({
        fullName: usuario.FullName || '',
        username: usuario.Username || '',
        email: usuario.Email || '',
        password: '',
        confirmPassword: '',
        rolId: rolEncontrado ? String(rolEncontrado.Id) : '',
        departamentoId: departamentoEncontrado ? String(departamentoEncontrado.Id) : '',
        hotelIds: hotelesDelUsuario.map((uh: any) => String(uh.HotelId)),
        jefeId: jefeActualId,
        departamentoIds: departamentosDelUsuario.map((ud: any) => String(ud.DepartamentoId)),
        moduloIds: modulosDelUsuario.map((um: any) => String(um.ModuloId)),
      });

      setEditandoId(usuario.Id);
      onOpen();
    } catch (error) {
      console.error(error);
      toast({ title: 'No se pudo cargar el usuario para editar', status: 'error', duration: 4000 });
    } finally {
      setCargandoEdicion(false);
    }
  };

  // Desactivar NO borra al usuario (queda todo su historial de resguardos,
  // firmas, etc. intacto) — solo le impide iniciar sesión (Status = 'inactive',
  // validado en el login) y deja de ofrecerlo como jefe/supervisor.
  const handleToggleEstado = async (usuario: any) => {
    const activo = usuario.Status === 'active';
    if (activo) {
      const confirmado = window.confirm(
        `¿Desactivar a ${usuario.FullName}? No podrá iniciar sesión hasta que lo reactives.`
      );
      if (!confirmado) return;
    }

    setCambiandoEstadoId(usuario.Id);
    try {
      const solicitanteId = getCurrentUser()?.Id;
      if (activo) {
        await usuariosApi.desactivar(usuario.Id, solicitanteId);
      } else {
        await usuariosApi.reactivar(usuario.Id, solicitanteId);
      }
      toast({
        title: activo ? 'Usuario desactivado' : 'Usuario reactivado',
        status: 'success',
        duration: 3000,
      });
      cargarCatalogos();
    } catch (error: any) {
      console.error(error);
      toast({
        title: `No se pudo ${activo ? 'desactivar' : 'reactivar'} el usuario`,
        description: error?.response?.data?.error,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setCambiandoEstadoId(null);
    }
  };

  const handleGuardarUsuario = async () => {
    const error = validarFormulario();
    if (error) {
      toast({ title: 'Revisa el formulario', description: error, status: 'warning', duration: 4000 });
      return;
    }

    const editando = editandoId != null;
    setIsSaving(true);
    try {
      const ahora = formatoFechaMySQL(new Date());
      const creadorId = getCurrentUser()?.Id;
      let usuarioId: number | string;

      if (editando) {
        usuarioId = editandoId as number | string;
        const usuarioActual = usuarios.find((u: any) => String(u.Id) === String(editandoId));
        await usuariosApi.editar(usuarioId, {
          FullName: form.fullName.trim(),
          Username: form.username.trim(),
          Email: form.email.trim(),
          // En blanco = no cambiar la contraseña actual.
          Password: form.password || usuarioActual?.Password,
          Status: usuarioActual?.Status || 'active',
          LastLogin: usuarioActual?.LastLogin || null,
          FailedLoginAttempts: usuarioActual?.FailedLoginAttempts ?? 0,
          PasswordResetToken: usuarioActual?.PasswordResetToken || null,
          PasswordResetExpires: usuarioActual?.PasswordResetExpires || null,
          LastModifiedDate: ahora,
          RolId: Number(form.rolId),
          DepartamentoId: Number(form.departamentoId),
          CreadorId: creadorId,
        });
      } else {
        usuarioId = usuarios.reduce((max, u) => Math.max(max, Number(u.Id) || 0), 0) + 1;
        await usuariosApi.crear({
          Id: usuarioId,
          FullName: form.fullName.trim(),
          Username: form.username.trim(),
          Email: form.email.trim(),
          Password: form.password,
          Status: 'active',
          LastLogin: null,
          FailedLoginAttempts: 0,
          PasswordResetToken: null,
          PasswordResetExpires: null,
          CreatedDate: ahora,
          LastModifiedDate: ahora,
          RolId: Number(form.rolId),
          DepartamentoId: Number(form.departamentoId),
          CreadorId: creadorId,
        });
      }

      const [hotelesFallidos, departamentosFallidos, modulosFallidos] = await Promise.all([
        sincronizarAsignaciones({
          originales: asignacionesOriginales.hoteles,
          campoId: 'HotelId',
          nuevos: form.hotelIds,
          usuarioId,
          ahora,
          crear: hotelesApi.crearUsuarioHotel,
          darBaja: hotelesApi.darBajaUsuarioHotel,
        }),
        sincronizarAsignaciones({
          originales: asignacionesOriginales.departamentos,
          campoId: 'DepartamentoId',
          nuevos: form.departamentoIds,
          usuarioId,
          ahora,
          crear: departamentosApi.crearUsuarioDepartamento,
          darBaja: departamentosApi.darBajaUsuarioDepartamento,
        }),
        sincronizarAsignaciones({
          originales: asignacionesOriginales.modulos,
          campoId: 'ModuloId',
          nuevos: form.moduloIds,
          usuarioId,
          ahora,
          crear: modulosApi.crearUsuarioModulo,
          darBaja: modulosApi.darBajaUsuarioModulo,
        }),
      ]);

      let jefeFallido = false;
      // Se manda tanto al crear como al editar, pero solo si el jefe
      // realmente cambió (evita una reasignación innecesaria si no se tocó).
      if (requiereJefe && form.jefeId && form.jefeId !== jefeOriginalId) {
        try {
          await usuariosApi.asignarSupervisor(form.jefeId, usuarioId);
        } catch (errJefe) {
          console.error(errJefe);
          jefeFallido = true;
        }
      }

      if (hotelesFallidos > 0 || departamentosFallidos > 0 || modulosFallidos > 0 || jefeFallido) {
        const detalles = [
          hotelesFallidos > 0 ? `${hotelesFallidos} hotel(es)` : null,
          departamentosFallidos > 0 ? `${departamentosFallidos} departamento(s) adicional(es)` : null,
          modulosFallidos > 0 ? `${modulosFallidos} módulo(s)` : null,
          jefeFallido ? 'el jefe/supervisor' : null,
        ].filter(Boolean).join(' y ');
        toast({
          title: `Usuario ${editando ? 'actualizado' : 'creado'} con advertencias`,
          description: `Los datos se guardaron, pero no se pudo ajustar ${detalles}. Revísalo manualmente.`,
          status: 'warning',
          duration: 6000,
        });
      } else {
        toast({
          title: `Usuario ${editando ? 'actualizado' : 'creado'}`,
          description: editando
            ? 'Los cambios se guardaron correctamente.'
            : 'El usuario, sus hoteles y su jefe (si aplicaba) se guardaron correctamente.',
          status: 'success',
          duration: 4000,
        });
      }

      cerrarModal();
      cargarCatalogos();
    } catch (err: any) {
      console.error(err);
      toast({
        title: `No se pudo ${editando ? 'actualizar' : 'crear'} el usuario`,
        description: err?.response?.data?.error || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const usuariosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return usuariosVisibles;
    return usuariosVisibles.filter((u: any) =>
      String(u.FullName || '').toLowerCase().includes(q) ||
      String(u.Username || '').toLowerCase().includes(q) ||
      String(u.Email || '').toLowerCase().includes(q) ||
      String(u.Rol || '').toLowerCase().includes(q)
    );
  }, [usuariosVisibles, busqueda]);

  if (!puedeAdministrarUsuarios()) {
    return (
      <Box p={6}>
        <Card variant="outline" bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <Text color="gray.500">Esta sección es exclusiva para administradores.</Text>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 6 }} pb={6}>
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} mb={6} gap={3}>
        <Box>
          <Heading size="lg">Usuarios por hotel</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Crea colaboradores y asígnales hotel, departamento y jefe directo.
          </Text>
        </Box>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onOpen} isDisabled={isLoadingCatalogos}>
          Nuevo usuario
        </Button>
      </Flex>

      <Card variant="outline" bg={cardBg} borderColor={borderColor}>
        <CardBody>
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
            <Heading size="sm" fontWeight="600">Usuarios existentes</Heading>
            <InputGroup maxW="280px" size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" boxSize={3.5} />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nombre, correo o rol…"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>
          </Flex>

          {isLoadingCatalogos ? (
            <Flex justify="center" py={12}><Spinner /></Flex>
          ) : usuariosFiltrados.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
              No se encontraron usuarios.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Usuario</Th>
                    <Th>Correo</Th>
                    <Th>Rol</Th>
                    <Th>Departamento</Th>
                    <Th>Estatus</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {usuariosFiltrados.map((u: any) => (
                    <Tr key={u.Id} _hover={{ bg: subtleBg }}>
                      <Td>
                        <HStack spacing={3}>
                          <Avatar size="sm" name={u.FullName} />
                          <Box>
                            <Text fontWeight="600" fontSize="sm">{u.FullName}</Text>
                            <Text fontSize="xs" color="gray.500">{u.Username}</Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td fontSize="sm" color="gray.600">{u.Email}</Td>
                      <Td>
                        <Badge
                          colorScheme={ROL_BADGE_COLOR[String(u.Rol || '').toLowerCase()] || 'gray'}
                          variant="subtle"
                          fontSize="2xs"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                        >
                          {u.Rol || 'Sin rol'}
                        </Badge>
                      </Td>
                      <Td fontSize="sm" color="gray.600">{u.Departamento || '—'}</Td>
                      <Td>
                        <Badge
                          colorScheme={u.Status === 'active' ? 'green' : 'gray'}
                          variant="subtle"
                          fontSize="2xs"
                          px={2}
                          py={0.5}
                          borderRadius="full"
                        >
                          {u.Status === 'active' ? 'Activo' : (u.Status || 'Inactivo')}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip
                            label="Solo el Administrador global puede editar a otro administrador"
                            isDisabled={puedeEditarUsuario(u)}
                            fontSize="xs"
                          >
                            <IconButton
                              aria-label="Editar usuario"
                              icon={<MdEdit size="18px" />}
                              size="sm"
                              variant="ghost"
                              isLoading={cargandoEdicion && editandoId === u.Id}
                              isDisabled={!puedeEditarUsuario(u)}
                              onClick={() => abrirEdicion(u)}
                            />
                          </Tooltip>
                          <Tooltip
                            label={
                              String(u.Id) === String(getCurrentUser()?.Id)
                                ? 'No puedes desactivarte a ti mismo'
                                : 'Solo el Administrador global puede cambiar el estado de otro administrador'
                            }
                            isDisabled={puedeCambiarEstadoDe(u)}
                            fontSize="xs"
                          >
                            <IconButton
                              aria-label={u.Status === 'active' ? 'Desactivar usuario' : 'Reactivar usuario'}
                              icon={u.Status === 'active' ? <MdBlock size="18px" /> : <MdCheckCircle size="18px" />}
                              size="sm"
                              variant="ghost"
                              colorScheme={u.Status === 'active' ? 'red' : 'green'}
                              isLoading={cambiandoEstadoId === u.Id}
                              isDisabled={!puedeCambiarEstadoDe(u)}
                              onClick={() => handleToggleEstado(u)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={cerrarModal} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editandoId != null ? 'Editar usuario' : 'Nuevo usuario'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={6} pb={2}>
              <Box>
                <SectionTitle icon={MdBadge}>Información básica</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Nombre completo</FormLabel>
                    <Input
                      value={form.fullName}
                      onChange={e => actualizarCampo('fullName', e.target.value)}
                      placeholder="Nombre y apellidos"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Usuario</FormLabel>
                    <Input
                      value={form.username}
                      onChange={e => actualizarCampo('username', e.target.value)}
                      placeholder="nombre.usuario"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box>
                <SectionTitle icon={MdLock}>Acceso</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired gridColumn={{ md: 'span 2' }}>
                    <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={e => actualizarCampo('email', e.target.value)}
                      placeholder="correo@velasresorts.com"
                    />
                  </FormControl>

                  <FormControl isRequired={editandoId == null}>
                    <FormLabel fontSize="sm">
                      Contraseña{editandoId != null && ' (déjalo vacío para no cambiarla)'}
                    </FormLabel>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => actualizarCampo('password', e.target.value)}
                      placeholder={editandoId != null ? 'Sin cambios' : 'Mínimo 6 caracteres'}
                    />
                  </FormControl>

                  <FormControl isRequired={editandoId == null || !!form.password}>
                    <FormLabel fontSize="sm">Confirmar contraseña</FormLabel>
                    <Input
                      type="password"
                      value={form.confirmPassword}
                      onChange={e => actualizarCampo('confirmPassword', e.target.value)}
                      placeholder="Repite la contraseña"
                      isDisabled={editandoId != null && !form.password}
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box>
                <SectionTitle icon={MdBusiness}>Rol y departamento</SectionTitle>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <SelectField
                    label="Rol"
                    required
                    mb="0"
                    placeholder="Busca un rol…"
                    options={rolesAsignables.map((r: any) => ({ value: String(r.Id), label: r.Name }))}
                    value={form.rolId}
                    onChange={(value: string) => actualizarCampo('rolId', value)}
                  />

                  <SelectField
                    label="Departamento"
                    required
                    mb="0"
                    placeholder="Busca un departamento…"
                    options={departamentos.map((d: any) => ({ value: String(d.Id), label: d.Name }))}
                    value={form.departamentoId}
                    onChange={(value: string) => actualizarCampo('departamentoId', value)}
                  />
                </SimpleGrid>

                {rolSeleccionado && (
                  <Flex align="start" gap={2} mt={3} p={3} bg={subtleBg} borderRadius="md">
                    <Icon as={MdInfoOutline} color="blue.500" boxSize={4} mt="2px" flexShrink={0} />
                    <Text fontSize="xs" color="gray.600">
                      {ROL_DESCRIPCION[String(rolSeleccionado.Name || '').toLowerCase()]
                        || `No hay una descripción registrada para el rol "${rolSeleccionado.Name}".`}
                    </Text>
                  </Flex>
                )}

                <FormControl isRequired mt={4}>
                  <FormLabel fontSize="sm">Hoteles asignados</FormLabel>
                  {hoteles.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No tienes hoteles asignados para poder crear usuarios.
                    </Text>
                  ) : (
                    <CheckboxGroup
                      value={form.hotelIds}
                      onChange={(valores) => actualizarCampo('hotelIds', valores as string[])}
                    >
                      <Wrap spacing={3}>
                        {hoteles.map((h: any) => (
                          <WrapItem key={h.Id}>
                            <Checkbox value={String(h.Id)}>{h.Name}</Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </CheckboxGroup>
                  )}
                </FormControl>
              </Box>

              <Box>
                <SectionTitle icon={MdApps}>Acceso avanzado (opcional)</SectionTitle>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Déjalo vacío para que el usuario vea todo lo normal según su rol. Si marcas
                  algo aquí, su acceso queda limitado exactamente a lo marcado.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <Flex justify="space-between" align="center" mb={1}>
                      <FormLabel fontSize="sm" mb={0}>Departamentos adicionales que puede ver</FormLabel>
                      {form.departamentoIds.length > 0 && (
                        <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
                          {form.departamentoIds.length}
                        </Badge>
                      )}
                    </Flex>
                    {departamentos.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">No hay departamentos disponibles.</Text>
                    ) : (
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={subtleBg}>
                        <Box px={3} pt={2}>
                          <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                              <SearchIcon color="gray.400" boxSize={3} />
                            </InputLeftElement>
                            <Input
                              placeholder="Buscar departamento…"
                              value={filtroDepartamentos}
                              onChange={e => setFiltroDepartamentos(e.target.value)}
                              bg={cardBg}
                            />
                          </InputGroup>
                        </Box>
                        <CheckboxGroup
                          value={form.departamentoIds}
                          onChange={(valores) => actualizarCampo('departamentoIds', valores as string[])}
                        >
                          <Stack spacing={1} maxH="180px" overflowY="auto" p={3}>
                            {departamentosFiltrados.length === 0 ? (
                              <Text fontSize="xs" color="gray.500">Sin resultados.</Text>
                            ) : (
                              departamentosFiltrados.map((d: any) => (
                                <Checkbox key={d.Id} value={String(d.Id)} fontSize="sm">
                                  {d.Name}
                                </Checkbox>
                              ))
                            )}
                          </Stack>
                        </CheckboxGroup>
                      </Box>
                    )}
                  </FormControl>

                  <FormControl>
                    <Flex justify="space-between" align="center" mb={1}>
                      <FormLabel fontSize="sm" mb={0}>Módulos a los que tiene acceso</FormLabel>
                      {form.moduloIds.length > 0 && (
                        <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
                          {form.moduloIds.length}
                        </Badge>
                      )}
                    </Flex>
                    {modulos.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">No hay módulos disponibles.</Text>
                    ) : (
                      <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={subtleBg} p={3}>
                        <CheckboxGroup
                          value={form.moduloIds}
                          onChange={(valores) => actualizarCampo('moduloIds', valores as string[])}
                        >
                          <Stack spacing={2} maxH="216px" overflowY="auto">
                            {modulos.map((m: any) => (
                              <Checkbox key={m.Id} value={String(m.Id)} alignItems="flex-start">
                                <Text fontSize="sm" fontWeight="600" lineHeight="1.2">{m.Nombre}</Text>
                                {m.Descripcion && (
                                  <Text fontSize="xs" color="gray.500" lineHeight="1.2">{m.Descripcion}</Text>
                                )}
                              </Checkbox>
                            ))}
                          </Stack>
                        </CheckboxGroup>
                      </Box>
                    )}
                  </FormControl>
                </SimpleGrid>
              </Box>

              {requiereJefe && (
                <Box>
                  <Divider mb={5} />
                  <SectionTitle icon={MdSupervisorAccount}>Jefe / Supervisor</SectionTitle>
                  <Text fontSize="xs" color="gray.500" mb={3}>
                    Es quien recibirá el correo para firmar cuando este colaborador cree un resguardo.
                  </Text>
                  {form.hotelIds.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Selecciona primero al menos un hotel para poder elegir al jefe.
                    </Text>
                  ) : jefesDisponibles.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No hay usuarios registrados en los hoteles seleccionados para asignar como jefe.
                    </Text>
                  ) : (
                    <SelectField
                      label="Jefe / Supervisor"
                      required
                      mb="0"
                      placeholder="Busca al jefe/supervisor…"
                      options={jefesDisponibles.map((j: any) => ({ value: String(j.Id), label: j.Name }))}
                      value={form.jefeId}
                      onChange={(value: string) => actualizarCampo('jefeId', value)}
                    />
                  )}
                </Box>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={cerrarModal}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleGuardarUsuario}
              isLoading={isSaving}
              loadingText={editandoId != null ? 'Guardando…' : 'Creando…'}
              isDisabled={hoteles.length === 0}
            >
              {editandoId != null ? 'Guardar cambios' : 'Crear usuario'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
